import React from 'react';
import { Box } from '@mui/material';
import {
  RowCell,
  CONTENT_CELL_WIDTH,
  ELEMENT_CO2_BAR_HEIGHT,
  ELEMENT_COST_BAR_HEIGHT,
  LIST_SPACING,
} from '@ui/ElementList';
import { SimpleBarChart } from '@ui/BarChart';
import { useDisplayCurrency } from '@features/currency/selected-display-currency.hook';
import { useCo2eVisible } from '@features/results/co2e-visible.hook';
import { useSbefGroupSectionBarsData } from '../hooks/use-sbef-group-section-bars-data.hook';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { Icon } from '@ui/Icon';
import { CompactValueDisplay } from '@features/projects/lists/CompactValueDisplay';
import type { GroupSectionData } from '@ui/ElementList/list.model';
import type { GroupSectionMeta } from '../element-grouping.model';
import type { OneOfListElements } from '@shared/models/project.model';

type SbefGroupSectionBarsAndMetricsProps = {
  section: GroupSectionData<OneOfListElements, GroupSectionMeta>;
  /** When false, metrics cell uses visibility: hidden (e.g. show on hover in list row). Default true. */
  metricsVisible?: boolean;
  /** When false, show only metric values (no metric icons). Default true. */
  showMetricIcons?: boolean;
  /** When true, renders kebab spacer for list row layout. Default false. */
  showKebabSpacer?: boolean;
};

export const SbefGroupSectionBarsAndMetrics: React.FC<
  SbefGroupSectionBarsAndMetricsProps
> = ({
  section,
  metricsVisible = true,
  showMetricIcons = true,
  showKebabSpacer = false,
}) => {
  const displayCurrency = useDisplayCurrency();
  const co2eVisible = useCo2eVisible();

  const { sekValues, co2eMax, sekMax, proposalBars, co2ePerGFA, sekPerGFA } =
    useSbefGroupSectionBarsData(section);

  return (
    <>
      <RowCell responsiveWidth={1}>
        <RowCell responsiveWidth={1}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            {co2eVisible.isEnabled &&
              proposalBars.map((bar) => (
                <Box key={bar.id}>
                  <SimpleBarChart
                    value={bar.co2Values}
                    maxValue={co2eMax}
                    height={ELEMENT_CO2_BAR_HEIGHT}
                    color={bar.color}
                  />
                </Box>
              ))}
            {displayCurrency.isEnabled && (
              <Box>
                <SimpleBarChart
                  value={sekValues}
                  maxValue={sekMax}
                  height={ELEMENT_COST_BAR_HEIGHT}
                  color="black"
                />
              </Box>
            )}
          </Box>
        </RowCell>
      </RowCell>
      <RowCell
        align="right"
        width={
          CONTENT_CELL_WIDTH.METRICS +
          CONTENT_CELL_WIDTH.ICON_KEBAB +
          LIST_SPACING.DEFAULT * 8
        }
        visibility={metricsVisible ? 'visible' : 'hidden'}
      >
        {co2eVisible.isEnabled && (
          <RowCell align="center" width={CONTENT_CELL_WIDTH.ICON_METRIC}>
            <CompactValueDisplay
              value={co2ePerGFA}
              unit="kgCO2e / m²"
              tooltipTitle="Emissions per GFA"
              icon={
                showMetricIcons ? (
                  <Icon category="costEmission" icon="kgCO2e" />
                ) : undefined
              }
            />
          </RowCell>
        )}
        {displayCurrency.isEnabled && (
          <RowCell align="center" width={CONTENT_CELL_WIDTH.ICON_METRIC}>
            <CompactValueDisplay
              value={
                getDisplayValueData(
                  { value: sekPerGFA, unit: 'sek' },
                  {
                    outputUnit: { unit: displayCurrency.current },
                    notation: 'compact',
                  },
                ).valueAsNumber
              }
              unit={`${displayCurrency.current} / m²`}
              tooltipTitle="Expenses per GFA"
              icon={
                showMetricIcons ? (
                  <Icon
                    category="currency"
                    icon={displayCurrency.current}
                    size={18}
                  />
                ) : undefined
              }
            />
          </RowCell>
        )}
        {showKebabSpacer && (
          <RowCell width={CONTENT_CELL_WIDTH.ICON_KEBAB} hideOnPrint />
        )}
      </RowCell>
    </>
  );
};
